// @ts-ignore
import {useRouter} from 'nextjs-toploader/app'

import {Product, ProductType} from '@/src/core/domain/entities/product'

import {isWeb} from '@/src/shared/utils/functions'
import {OAuthProvider, settings} from '@/src/config/settings'

export const Routes = {
  auth: {
    login: {
      page: '/login/',
    },
    logout: {
      page: '/logout/',
    },
  },
  static(url: string, staticPages: string[]) {
    if (isWeb()) return url
    if (staticPages.includes(url) || staticPages.map(x => `${x}/`).includes(url)) {
      return `/static/?path=${url}`
    }
    return url
  },
  certificates: {
    page: '/certificates/',
  },
  catalog: {
    page: '/catalog/',
    sale: {
      page: '/sale/',
    },
    section: {
      page: (sectionSlug: string, forceMobile = false) =>
        isWeb() && !forceMobile ? `/catalog/${sectionSlug}` : `/catalog/?slug=${sectionSlug}`,
    },
    product: {
      page(productCode: string, type: Product['type'], forceMobile = false) {
        return type === ProductType.Certificate
          ? Routes.certificates.page
          : isWeb() && !forceMobile
            ? `/product/${productCode}/`
            : `/product/?code=${productCode}`
      },
    },
  },
  profile: {
    page: '/profile/',
    personal: {
      page: '/profile/personal/',
    },
    bonus: {
      page: '/profile/bonus/',
    },
    orders: {
      page: '/profile/orders/',
    },
    service: {
      records: {
        page: '/profile/service-records/',
      },
    },
    favourite: {
      page: '/profile/favourite/',
    },
  },
  favourite: {
    page: '/favourite/',
  },
  basket: {
    page: '/cart/',
  },
  order: {
    page: '/order/',
    successPage: '/order/success/?order_id=<order_id>',
  },
  brands: {
    page: '/brands/',
    detail(code: string, forceMobile = false) {
      return isWeb() && !forceMobile ? `${this.page}${code}/` : `${this.page}?slug=${code}`
    },
  },
  service: {
    shoeTesting: '/service/shoe-testing/',
    functionalTesting: '/service/func-testing/',
  },
  info: {
    runningClub: '/info/running-club/',
    shipmentInfo: '/info/shipment-payment/',
  },
  news: {
    page: '/news/',
    detail: {
      page: (code: string, forceMobile = false) =>
        isWeb() && !forceMobile ? `/news/${code}/` : `/news/?code=${code}`,
    },
    section: {
      page: (slug: string, forceMobile = false) =>
        isWeb() && !forceMobile ? `/news/${slug}/` : `/news/?slug=${slug}`,
    },
  },
  articles: {
    page: '/articles/',
    section: {
      page: (slug: string, forceMobile = false) =>
        isWeb() && !forceMobile ? `/articles/${slug}/` : `/articles/?slug=${slug}`,
    },
    article: {
      page: (code: string, forceMobile = false) =>
        isWeb() && !forceMobile ? `/article/${code}/` : `/article/?code=${code}`,
    },
  },
  video: {
    page: '/video/',
  },
  userAgree: '/user-agree/',
  contacts: {
    page: '/contacts/',
  },
  bugReport: {
    page: '/bug-report/',
  },
  webApi: {
    auth: {
      sendOTP: '/v1/auth/send-otp',
      confirmOTP: '/v1/auth/confirm-otp',
    },
    page: {
      data: '/v1/page',
      meta: '/v1/page/meta',
    },
    layout: {
      data: '/v1/layout',
    },
    basket: {
      add: '/v1/cart/add',
      sync: '/v1/cart/sync',
      delete: '/v1/cart/delete',
    },
    order: {
      deliveryTypes: '/v1/order/services/delivery',
      deliveryTypeDetails: '/v1/order/services/delivery/relations',
      totals: '/v1/order/totals',
      payTypes: '/v1/order/services/payment',
      create: '/v1/order/create',
      certificate: {
        create: '/v1/order/certificate/create',
      },
    },
    profile: {
      me: '/v1/profile/me',
      order: {
        details: (id: Id) => `/v1/order/by/${id}`,
      },
      personal: {
        edit: '/v1/profile/change',
      },
      bonus: {
        editBonusCard: '/v1/profile/bonus-account/change',
      },
    },
    form: {
      testing: {
        shopRules: (shopId: Id) => `/v1/form/func-testing/lab/rules/${shopId}`,
        shopTimeSlots: (shopId: Id) => `/v1/form/func-testing/lab/slots/${shopId}`,
        save: '/v1/form/func-testing/booking',
      },
      shoe: {
        shopRules: (shopId: Id) => `/v1/form/shoe-fitting/lab/rules/${shopId}`,
        shopTimeSlots: (shopId: Id) => `/v1/form/shoe-fitting/lab/slots/${shopId}`,
        save: '/v1/form/shoe-fitting/booking',
      },
      callback: {
        save: '/v1/form/feedback/callback/create',
      },
      question: {
        save: '/v1/form/feedback/question/create',
      },
      product: {
        fastBuy: {
          save: '/v1/form/fast-order/create',
        },
        notAvailable: {
          save: '/v1/form/feedback/product-request/create',
        },
      },
      bugReport: {
        create: '/v1/form/feedback/bug-report/create',
      },
    },
    products: {
      list: '/v1/catalog/products/by/ids',
      byExternalId: '/v1/catalog/products/by/external-id',
    },
    subscribe: '/v1/subscription/subscribe',
  },
  api: {
    favourite: '/api/favourite/',
    auth: {
      login: {
        receiveCode: '/api/auth/login/',
        sendCode: '/api/auth/login/code/',
      },
      logout: '/api/auth/logout/',
      checkStatus: '/api/auth/status/',
      updateSession: '/api/update-session/',
      refreshSession: '/api/refresh-session-user/',
      thirdParty: {
        start: (provider: ValuesOf<typeof OAuthProvider>) => `/api/oauth/${provider}/start/`,
        callback: (provider: ValuesOf<typeof OAuthProvider>) => `/api/oauth/${provider}/callback/`,
      },
    },
    data: {
      getData: '/api/data/',
      transform: '/api/data/transform/',
    },
    order: {
      companyInfo: '/api/order/company/',
    },
    daDataAddress: {
      search: '/api/address/search/',
    },
    daDataCustomLocation: {
      search: '/api/custom-location/search/',
    },
    profile: {
      delete: '/api/profile/delete/',
    },
    inner: {
      instrumentation: {
        traces: '/api/instrumentation/traces/',
        logs: '/api/instrumentation/logs/',
      },
    },
    expternal: {
      oauth: {
        start: (provider: ValuesOf<typeof OAuthProvider>) =>
          `${settings.NEXT_OAUTH_ADDRESS}/oauth/${provider}/start`,
        callback: (provider: ValuesOf<typeof OAuthProvider>) =>
          `${settings.NEXT_OAUTH_ADDRESS}/oauth/${provider}/callback`,
      },
    },
  },
  vendor: {
    daData: {
      companyInfo: 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/party',
      bankInfo: 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/bank',
      address: 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address',
    },
  },
}

export const useRouting = () => {
  const router = useRouter()

  const go = (href: string) => {
    router.push(href)
  }

  return {
    go,
  }
}
